import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDeleteQuote, useMe, useQuotes, useSubmitQuote } from '../api/hooks';
import { StatusBadge } from '../components/StatusBadge';
import { DraftBanner } from '../components/DraftBanner';
import { fmtDate, fmtMoney } from '../lib/format';

export function QuotesPage() {
  const quotes = useQuotes();
  const me = useMe();
  const navigate = useNavigate();
  const submit = useSubmitQuote();
  const del = useDeleteQuote();
  const isApprover = me.data?.user?.role === 'approver';
  const assistantEnabled = me.data?.assistantEnabled ?? false;
  const [err, setErr] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');

  return (
    <>
      <div className="page-head">
        <h1>Quotes</h1>
        <Link className="btn primary" to="/quotes/new">
          New Quote
        </Link>
      </div>

      <DraftBanner kind="quote" to="/quotes/new" label="quote" />

      {assistantEnabled && (
        <div className="assistant-card">
          <div className="assistant-head">✨ Create with AI — describe a customer, items, and/or a quote in one prompt</div>
          <div className="assistant-row">
            <input
              value={prompt}
              placeholder="e.g. “Quote Bearston Law 10 hours of Discovery Review at $250, our cost $150.”"
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && prompt.trim()) navigate('/create', { state: { prompt } });
              }}
              style={{ flex: 1 }}
            />
            <button type="button" className="primary" onClick={() => navigate('/create', { state: { prompt: prompt.trim() || undefined } })}>
              {prompt.trim() ? 'Generate plan' : 'Open'}
            </button>
          </div>
        </div>
      )}
      <div className="card">
        {err && <div className="banner error">{err}</div>}
        {quotes.isLoading && <p className="muted">Loading…</p>}
        {quotes.error && <p className="error-text">{(quotes.error as Error).message}</p>}
        {quotes.data && quotes.data.length === 0 && <p className="muted">No quotes yet. Create your first one.</p>}
        {quotes.data && quotes.data.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Quote #</th>
                  <th>Customer</th>
                  <th>By</th>
                  <th>Status</th>
                  <th className="right">Total</th>
                  <th className="right">Margin</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {quotes.data.map((q) => (
                  <tr key={q.id}>
                    <td>
                      <Link to={`/quotes/${q.id}`}>{q.quoteNumber}</Link>
                    </td>
                    <td>{q.customerName ?? '—'}</td>
                    <td>{q.creatorFirstName ?? '—'}</td>
                    <td>
                      <StatusBadge status={q.status} />
                    </td>
                    <td className="right">{fmtMoney(q.totalAmount, q.currency)}</td>
                    <td className="right" style={{ color: q.totalMargin >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>
                      {fmtMoney(q.totalMargin, q.currency)}
                    </td>
                    <td>{fmtDate(q.createdAt)}</td>
                    <td className="right btn-row" style={{ justifyContent: 'flex-end' }}>
                      {isApprover && q.status === 'Created' && (
                        <button
                          className="primary"
                          onClick={() => {
                            setErr(null);
                            submit.mutate(q.id, { onError: (e) => setErr((e as Error).message) });
                          }}
                          disabled={submit.isPending}
                        >
                          Submit
                        </button>
                      )}
                      {q.status !== 'Closed' && (
                        <button
                          className="link"
                          onClick={() => {
                            setErr(null);
                            if (window.confirm(`Delete ${q.quoteNumber}?`)) del.mutate(q.id, { onError: (e) => setErr((e as Error).message) });
                          }}
                          disabled={del.isPending}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
